/**
 * Binom Campaign sub-tab — Main Options form (Name, Group, Tags, Traffic
 * Source, Cost type, Hide referrer, Domain, Type of distribution, Campaign
 * URL). Reads dropdown sources from `/binom/{traffic-sources,groups,domains,tags}`.
 *
 * Edits patch `draft.campaignMeta`; the parent's Save buttons forward the
 * non-empty diff as `campaignMeta` to PUT /binom/flows/:id.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';

import { Input } from '@/components/ui/form/input/Input';
import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import {
  useBinomDomains,
  useBinomGroups,
  useBinomTrafficSources,
} from '@/hooks/binom';

import type {
  BinomCampaignMetaDraft,
  BinomFlowDraft,
} from '../../binom-flow-draft.types';

import BinomCampaignFormRow from './BinomCampaignFormRow';
import BinomCampaignUrlField from './BinomCampaignUrlField';
import BinomCostHideRows from './cost/BinomCostHideRows';
import BinomTagsField from './tags/BinomTagsField';

export interface BinomCampaignConfigTabProps {
  draft: BinomFlowDraft;
  patchCampaignMeta: (m: (d: BinomCampaignMetaDraft) => void) => void;
}

const DISTRIBUTION_OPTIONS = [
  { value: 'SMART_PATHS_LPS_OFFERS', label: 'Smart rotation Path & LPs & Offers' },
  { value: 'ROTATION', label: 'Rotation (paths only)' },
  { value: 'DIRECT', label: 'Direct (single path)' },
];

const BinomCampaignConfigTab: FC<BinomCampaignConfigTabProps> = ({
  draft,
  patchCampaignMeta,
}) => {
  const meta = draft.campaignMeta;
  const groupsQuery = useBinomGroups();
  const sourcesQuery = useBinomTrafficSources();
  const domainsQuery = useBinomDomains();

  const groupOptions = useMemo(() => {
    const list = Array.isArray(groupsQuery.data) ? groupsQuery.data : [];
    return list.map((g) => ({ value: g.uuid, label: g.name }));
  }, [groupsQuery.data]);
  const sourceOptions = useMemo(() => {
    const list = Array.isArray(sourcesQuery.data) ? sourcesQuery.data : [];
    return list.map((s) => ({ value: s.id, label: s.name }));
  }, [sourcesQuery.data]);
  const domainOptions = useMemo(() => {
    const list = Array.isArray(domainsQuery.data) ? domainsQuery.data : [];
    return list.map((d) => ({ value: d.uuid, label: d.host }));
  }, [domainsQuery.data]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
      <BinomCampaignFormRow label="Name">
        <Input
          value={meta.name ?? ''}
          onChange={(e) => patchCampaignMeta((d) => (d.name = e.target.value))}
          className="h-7 text-xs"
        />
      </BinomCampaignFormRow>
      <BinomCampaignFormRow label="Group">
        <SearchableSelect
          value={meta.groupUuid ?? ''}
          onChange={(v) => patchCampaignMeta((d) => (d.groupUuid = String(v)))}
          options={[{ value: '', label: 'Choose group' }, ...groupOptions]}
        />
      </BinomCampaignFormRow>
      <BinomCampaignFormRow label="Tags">
        <BinomTagsField
          value={meta.tags ?? []}
          onChange={(next) =>
            patchCampaignMeta((d) => {
              d.tags = next;
            })
          }
        />
      </BinomCampaignFormRow>
      <BinomCampaignFormRow label="Traffic Source">
        <SearchableSelect
          value={meta.trafficSourceId != null ? String(meta.trafficSourceId) : ''}
          onChange={(v) =>
            patchCampaignMeta((d) => {
              d.trafficSourceId = v ? Number(v) : undefined;
            })
          }
          options={[{ value: '', label: 'No traffic source' }, ...sourceOptions]}
        />
      </BinomCampaignFormRow>
      <BinomCostHideRows meta={meta} patchCampaignMeta={patchCampaignMeta} />
      <BinomCampaignFormRow label="Domain">
        <SearchableSelect
          value={meta.domainUuid ?? ''}
          onChange={(v) => patchCampaignMeta((d) => (d.domainUuid = String(v)))}
          options={[{ value: '', label: 'No domain' }, ...domainOptions]}
        />
      </BinomCampaignFormRow>
      <BinomCampaignFormRow label="Type of distribution">
        <SearchableSelect
          value={meta.distributionType ?? ''}
          onChange={(v) =>
            patchCampaignMeta((d) => (d.distributionType = String(v)))
          }
          options={[{ value: '', label: 'Choose type' }, ...DISTRIBUTION_OPTIONS]}
        />
      </BinomCampaignFormRow>
      <BinomCampaignFormRow label="Campaign URL">
        <BinomCampaignUrlField
          domainUuid={meta.domainUuid}
          campaignKey={meta.key}
        />
      </BinomCampaignFormRow>
    </div>
  );
};

export default BinomCampaignConfigTab;
